import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, FileWarning, Loader2, CalendarDays, PoundSterling, FileText, ScrollText, MessageCircleQuestion, ListChecks, Lightbulb } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LOGO = "https://content.webfactorysite.co.uk/20260113092111-Wales-Event-Logo.png?t=1768296071";
const GRADE_LABELS = { first_aider: "First Aider", level_3: "Level 3", level_4: "Level 4", technician: "Technician", paramedic: "Paramedic", doctor: "Doctor" };

const RichText = ({ text, cls = "text-slate-300" }) => {
  const blocks = [];
  let buf = [];
  (text || "").split("\n").forEach((l) => {
    const m = l.match(/^\s*[-•*]\s+(.*)/);
    if (m) buf.push(m[1]);
    else {
      if (buf.length) { blocks.push({ bullets: buf }); buf = []; }
      if (l.trim()) blocks.push({ p: l });
    }
  });
  if (buf.length) blocks.push({ bullets: buf });
  return (
    <div className="space-y-1.5">
      {blocks.map((b, i) => (b.bullets ? (
        <ul key={i} className={`list-disc list-inside text-sm leading-relaxed space-y-0.5 ml-1 ${cls}`}>
          {b.bullets.map((x, j) => <li key={j}>{x}</li>)}
        </ul>
      ) : (
        <p key={i} className={`text-sm leading-relaxed ${cls}`}>{b.p}</p>
      )))}
    </div>
  );
};

export default function PublicProposal() {
  const { token } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [acceptedMsg, setAcceptedMsg] = useState("");
  const [acceptErr, setAcceptErr] = useState("");
  const [questions, setQuestions] = useState({});
  const [askOpen, setAskOpen] = useState({});
  const [comment, setComment] = useState("");
  const [sendingQ, setSendingQ] = useState(false);
  const [qMsg, setQMsg] = useState("");
  const [qErr, setQErr] = useState("");

  useEffect(() => {
    document.title = "Your Proposal — Wales Event Medical Services";
    fetch(`${BACKEND}/api/public/proposal/${token}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("bad"))))
      .then(setP)
      .catch(() => setErr("This proposal link isn't valid — please contact Wales Event Medical Services."));
  }, [token]);

  const accept = async () => {
    if (!name.trim()) { setAcceptErr("Please type your name to accept"); return; }
    setAccepting(true);
    setAcceptErr("");
    try {
      const res = await fetch(`${BACKEND}/api/public/proposal/${token}/accept`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Couldn't accept right now — please try again");
      setAcceptedMsg(data.message);
      setP((x) => ({ ...x, status: "accepted", accepted_by: name }));
    } catch (e) { setAcceptErr(e.message); }
    setAccepting(false);
  };

  if (err) {
    return (
      <div className="min-h-screen bg-[#0F2547] grid place-items-center p-6 text-center" data-testid="proposal-error">
        <div>
          <FileWarning className="h-10 w-10 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-300 text-sm">{err}</p>
        </div>
      </div>
    );
  }
  if (!p) return <div className="min-h-screen bg-[#0F2547] grid place-items-center"><Loader2 className="h-8 w-8 text-[#00D2C2] animate-spin" /></div>;

  const theme = p.theme || {};
  const accent = theme.accent_color || "#00D2C2";
  const dark = theme.secondary_color || "#0F2547";
  const pricedDays = (p.schedule || []).filter((d) => Number(d.price) > 0);
  const inputCls = "w-full h-12 rounded-lg border border-slate-600 bg-[#121820] px-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2";
  const isDone = p.status === "accepted" || acceptedMsg;
  const setQ = (k, refLabel, text) => setQuestions((q) => ({ ...q, [k]: { ref: refLabel, text } }));
  const AskBox = ({ k, refLabel }) => (
    isDone ? null : (
      <div className="mt-1">
        {!askOpen[k] ? (
          <button type="button" onClick={() => setAskOpen((o) => ({ ...o, [k]: true }))}
            className="text-[11px] font-bold inline-flex items-center gap-1 hover:underline" style={{ color: accent }} data-testid={`${k}-ask`}>
            <MessageCircleQuestion size={13} /> Question this
          </button>
        ) : (
          <textarea rows={2} value={questions[k]?.text || ""} onChange={(e) => setQ(k, refLabel, e.target.value)}
            placeholder={`Your question about "${refLabel}"…`} className={`${inputCls} h-auto py-2 mt-1`} data-testid={`${k}-askbox`} />
        )}
      </div>
    )
  );
  const sendQuery = async () => {
    const items = Object.values(questions).filter((q) => q.text.trim()).map((q) => ({ ref: q.ref, text: q.text.trim() }));
    if (!items.length && !comment.trim()) { setQErr("Tell us what you'd like changed — question an item above or add a note here"); return; }
    setSendingQ(true); setQErr("");
    try {
      const res = await fetch(`${BACKEND}/api/public/proposal/${token}/query`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, comment, items }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Couldn't send right now — please try again");
      setQMsg(data.message);
      setP((x) => ({ ...x, status: "changes_requested" }));
    } catch (e) { setQErr(e.message); }
    setSendingQ(false);
  };

  return (
    <div className="min-h-screen bg-[#0B0F14] text-slate-200" data-testid="public-proposal">
      {theme.image && (
        <div className="relative h-52 sm:h-72 overflow-hidden" data-testid="proposal-hero">
          <img src={theme.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to top, #0B0F14 4%, ${dark}66 55%, transparent)` }} />
          <div className="absolute bottom-0 left-0 right-0 max-w-3xl mx-auto px-4 sm:px-8 pb-5">
            <img src={LOGO} alt="Wales Event Medical Services" className="h-10 w-auto bg-white rounded-lg px-2 py-1 mb-3" />
            {theme.headline && <h1 className="text-white font-extrabold text-2xl sm:text-4xl leading-tight drop-shadow" data-testid="proposal-headline">{theme.headline}</h1>}
            {theme.tagline && <p className="text-sm sm:text-base text-slate-200 mt-1 drop-shadow" data-testid="proposal-tagline">{theme.tagline}</p>}
          </div>
        </div>
      )}

      <header className="border-b border-white/10 px-4 sm:px-8 py-5 flex items-center gap-3" style={theme.image ? { backgroundColor: dark } : undefined}>
        {!theme.image && <img src={LOGO} alt="Wales Event Medical Services" className="h-11 w-auto bg-white rounded-lg px-2 py-1" />}
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: accent }}>Proposal</p>
          <h1 className="text-white font-bold text-base sm:text-lg truncate" data-testid="proposal-title">{p.event_name || p.client_name}</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-8 space-y-6">
        <p className="text-sm text-slate-400" data-testid="proposal-for">Prepared for <strong className="text-white">{p.client_name}</strong> by Wales Event Medical Services</p>

        {p.overview && (
          <section className="rounded-2xl border border-slate-700 bg-[#0F141B] p-6" data-testid="proposal-overview">
            <h2 className="flex items-center gap-2 text-white font-bold mb-3"><FileText size={16} style={{ color: accent }} /> Proposal</h2>
            <RichText text={p.overview} />
          </section>
        )}

        {p.schedule?.length > 0 && (
          <section className="rounded-2xl border border-slate-700 bg-[#0F141B] p-6" data-testid="proposal-schedule">
            <h2 className="flex items-center gap-2 text-white font-bold mb-3"><CalendarDays size={16} style={{ color: accent }} /> Schedule of cover</h2>
            <div className="space-y-3">
              {p.schedule.map((d, i) => (
                <div key={i} className="rounded-xl border border-slate-700 bg-[#121820] p-4" data-testid={`proposal-day-${i}`}>
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    {d.kind && d.kind !== "day" && (
                      <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: `${accent}22`, color: accent }} data-testid={`proposal-day-kind-${i}`}>{d.kind === "24h" ? "24-hour" : "Night"}</span>
                    )}
                    <p className="font-bold text-white text-sm">{d.label || `Day ${i + 1}`}</p>
                    {d.date && <p className="text-xs font-bold" style={{ color: accent }}>{d.date}{d.end_date && d.end_date !== d.date ? ` → ${d.end_date}` : ""}</p>}
                    {d.times && <p className="text-xs text-slate-400">{d.times}{d.day_hours || d.night_hours ? ` · ${Number(d.day_hours) || 0}h day${Number(d.night_hours) ? ` + ${d.night_hours}h night` : ""}` : d.hours ? ` · ${d.hours}h` : ""}</p>}
                  </div>
                  {d.cover && <p className="text-sm text-slate-300 mt-1.5">{d.cover}</p>}
                  {d.included && <div className="mt-1.5" data-testid={`proposal-day-included-${i}`}><RichText text={d.included} cls="text-slate-400" /></div>}
                  {(() => {
                    const res = [...new Set([...(d.vehicles || []).map((v) => v.name), d.vehicle_name].filter(Boolean))]
                      .concat((d.equipment || []).filter((e) => e.name).map((e) => `${Number(e.qty) > 1 ? `${e.qty}× ` : ""}${e.name}`))
                      .concat(Object.entries(d.staffing || {}).filter(([, n]) => Number(n) > 0).map(([g, n]) => `${n}× ${GRADE_LABELS[g] || g}`));
                    return res.length > 0 && (
                      <p className="text-xs font-bold mt-1.5" style={{ color: accent }} data-testid={`proposal-day-resources-${i}`}>{res.join(" · ")}</p>
                    );
                  })()}
                  <AskBox k={`day-${i}`} refLabel={d.label || `Day ${i + 1}`} />
                </div>
              ))}
            </div>
          </section>
        )}

        {p.requirements && (
          <section className="rounded-2xl border border-slate-700 bg-[#0F141B] p-6" data-testid="proposal-requirements">
            <h2 className="flex items-center gap-2 text-white font-bold mb-3"><ListChecks size={16} style={{ color: accent }} /> What we require from you</h2>
            <RichText text={p.requirements} />
          </section>
        )}

        {p.suggestions && (
          <section className="rounded-2xl border border-slate-700 bg-[#0F141B] p-6" data-testid="proposal-suggestions">
            <h2 className="flex items-center gap-2 text-white font-bold mb-3"><Lightbulb size={16} style={{ color: accent }} /> What we'd recommend</h2>
            <RichText text={p.suggestions} />
          </section>
        )}

        {(p.pricing?.length > 0 || pricedDays.length > 0 || (p.extras || []).length > 0) && (
          <section className="rounded-2xl border border-slate-700 bg-[#0F141B] p-6" data-testid="proposal-pricing">
            <h2 className="flex items-center gap-2 text-white font-bold mb-3"><PoundSterling size={16} style={{ color: accent }} /> Pricing</h2>
            <div className="divide-y divide-slate-800">
              {pricedDays.map((d, i) => (
                <div key={`d${i}`} className="py-2.5" data-testid={`proposal-dayprice-${i}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="text-sm text-slate-300">{d.label || `Day ${i + 1}`}{d.date ? ` — ${d.date}` : ""}</p>
                    <p className="text-sm font-bold text-white shrink-0">£{Number(d.price).toFixed(2)}</p>
                  </div>
                  <AskBox k={`dayprice-${i}`} refLabel={d.label || `Day ${i + 1}`} />
                </div>
              ))}
              {(p.pricing || []).map((l, i) => (
                <div key={`l${i}`} className="py-2.5" data-testid={`proposal-line-${i}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-slate-300">{l.description}</p>
                      <p className="text-xs text-slate-500">£{Number(l.unit_price || 0).toFixed(2)}{l.per ? ` per ${l.per}` : ""}{Number(l.qty) !== 1 ? ` × ${l.qty}` : ""}</p>
                    </div>
                    <p className="text-sm font-bold text-white shrink-0">£{(Number(l.qty || 0) * Number(l.unit_price || 0)).toFixed(2)}</p>
                  </div>
                  <AskBox k={`line-${i}`} refLabel={l.description || "this item"} />
                </div>
              ))}
              {(p.extras || []).length > 0 && (
                <p className="pt-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">Additional costs</p>
              )}
              {(p.extras || []).map((l, i) => (
                <div key={`x${i}`} className="py-2.5" data-testid={`proposal-extra-${i}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-slate-300">{l.description}</p>
                      <p className="text-xs text-slate-500">£{Number(l.unit_price || 0).toFixed(2)}{l.per ? ` per ${l.per}` : ""}{Number(l.qty) !== 1 ? ` × ${l.qty}` : ""}</p>
                    </div>
                    <p className="text-sm font-bold text-white shrink-0">£{(Number(l.qty || 0) * Number(l.unit_price || 0)).toFixed(2)}</p>
                  </div>
                  <AskBox k={`extra-${i}`} refLabel={l.description || "this item"} />
                </div>
              ))}
              {(p.discount_amount || 0) > 0 && (
                <div className="py-2.5 flex items-baseline justify-between gap-3" data-testid="proposal-discount-row">
                  <p className="text-sm font-semibold text-[#F87171]">
                    Discount{p.discount_type === "percent" && p.discount_value ? ` (${p.discount_value}%)` : ""}
                  </p>
                  <p className="text-sm font-bold text-[#F87171] shrink-0">−£{p.discount_amount.toFixed(2)}</p>
                </div>
              )}
            </div>
            <div className="mt-4 pt-3 border-t border-slate-700 flex items-baseline justify-between">
              <p className="font-bold text-white">Total</p>
              <p className="text-xl font-extrabold" style={{ color: accent }} data-testid="proposal-total">£{p.total.toFixed(2)}</p>
            </div>
          </section>
        )}

        {p.terms && (
          <section className="rounded-2xl border border-slate-700 bg-[#0F141B] p-6" data-testid="proposal-terms">
            <h2 className="flex items-center gap-2 text-white font-bold mb-3"><ScrollText size={16} style={{ color: accent }} /> Terms</h2>
            <p className="text-sm leading-relaxed text-slate-300 whitespace-pre-wrap">{p.terms}</p>
          </section>
        )}

        <section className="rounded-2xl border-2 bg-[#0F141B] p-6" style={{ borderColor: `${accent}66` }} data-testid="proposal-formal-quote">
          <h2 className="text-white font-bold mb-1">Formal quote</h2>
          {p.quote_note && <p className="text-sm text-slate-300 whitespace-pre-wrap mb-3">{p.quote_note}</p>}
          <p className="text-2xl font-extrabold text-white">£{p.total.toFixed(2)} <span className="text-xs font-semibold text-slate-400">total for the cover above</span></p>

          {p.status === "accepted" || acceptedMsg ? (
            <div className="mt-5 rounded-xl border border-[#10B981]/40 bg-[#10B981]/10 p-4 flex items-start gap-2.5" data-testid="proposal-accepted">
              <CheckCircle2 size={18} className="text-[#34d399] shrink-0 mt-0.5" />
              <p className="text-sm text-[#34d399]">{acceptedMsg || `Accepted${p.accepted_by ? ` by ${p.accepted_by}` : ""} — thank you. We'll be in touch to confirm.`}</p>
            </div>
          ) : (
            <div className="mt-5 space-y-3">
              <p className="text-xs text-slate-400">Happy to go ahead? Type your name and accept — the dates go straight onto our calendar and we'll confirm with you.</p>
              {p.status === "changes_requested" && !qMsg && (
                <p className="text-xs font-semibold text-amber-400" data-testid="proposal-queried-note">Your questions have been sent — we'll come back to you. You can still accept below if you're happy.</p>
              )}
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" className={inputCls} data-testid="proposal-accept-name" />
              {acceptErr && <p className="text-xs text-red-400" data-testid="proposal-accept-error">{acceptErr}</p>}
              <button onClick={accept} disabled={accepting} data-testid="proposal-accept-btn"
                className="w-full h-12 rounded-full hover:opacity-90 disabled:opacity-50 text-[#06201D] text-sm font-extrabold inline-flex items-center justify-center gap-2 transition-opacity"
                style={{ backgroundColor: accent }}>
                {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 size={16} />} Accept this proposal
              </button>
              <div className="pt-3 border-t border-slate-700 space-y-2.5">
                <p className="text-xs font-bold text-slate-300">Not quite right? Tell us what to amend — we'd rather adjust it than lose you.</p>
                <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
                  placeholder="What would you like changed? (you can also question any item above)" className={`${inputCls} h-auto py-2`} data-testid="proposal-comment" />
                {qErr && <p className="text-xs text-red-400" data-testid="proposal-query-error">{qErr}</p>}
                {qMsg ? (
                  <p className="text-xs font-semibold text-[#34d399]" data-testid="proposal-query-sent">{qMsg}</p>
                ) : (
                  <button onClick={sendQuery} disabled={sendingQ} data-testid="proposal-query-btn"
                    className="w-full h-12 rounded-full border border-slate-500 hover:border-slate-300 disabled:opacity-50 text-slate-200 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors">
                    {sendingQ ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircleQuestion size={16} />} Send questions / request changes
                  </button>
                )}
              </div>
            </div>
          )}
        </section>

        {p.show_suppliers !== false && (
          <a href="/people-we-trust" target="_blank" rel="noreferrer" data-testid="proposal-suppliers-link"
            className="block rounded-2xl border border-slate-700 bg-[#0F141B] p-5 text-center hover:border-slate-500 transition-colors">
            <p className="text-sm font-bold text-white">Suppliers we like to work with</p>
            <p className="text-xs text-slate-400 mt-1">Security, catering, safety &amp; more — trusted teams we've worked alongside →</p>
          </a>
        )}

        <p className="text-center text-[11px] text-slate-500 pb-6">Wales Event Medical Services · Professional event medical cover · info@waleseventmedical.co.uk</p>
      </main>
    </div>
  );
}
